'use client';

import Cookies from 'js-cookie';
import { createContext, type PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import type { ColorsTheme, ColorsThemePreference } from '@alltrails/types/ColorsTheme';
import { COLORS_THEME_ATTRIBUTE, COLORS_THEME_COOKIE_NAME, colorsThemeCookieAttributes, DARK_COLORS_MEDIA_QUERY } from '../../utils/colorsTheme';

type ColorsThemeContextValue = {
  /** The palette actually being rendered, with 'system' already resolved against the OS setting. */
  colorsTheme: ColorsTheme;
  /** What the user chose. 'system' until they pick one, which is also what clearing their choice returns to. */
  colorsThemePreference: ColorsThemePreference;
  setColorsTheme: (colorsThemePreference: ColorsThemePreference) => void;
};

/**
 * Defaults to light so useColorsTheme works on surfaces that never opted into theming. RootLayout only renders the
 * provider when it is given a `colorsTheme`, so on those pages the setter is a no-op rather than a way to theme a
 * page nothing was designed against.
 */
export const ColorsThemeContext = createContext<ColorsThemeContextValue>({
  colorsTheme: 'light',
  colorsThemePreference: 'system',
  setColorsTheme: () => {}
});

type Props = {
  /** The preference the server rendered with, so the first client render agrees with the markup. */
  colorsThemePreference: ColorsThemePreference;
};

export default function ColorsThemeProvider({ colorsThemePreference: serverPreference, children }: PropsWithChildren<Props>) {
  const [colorsThemePreference, setColorsThemePreference] = useState(serverPreference);
  // The server cannot read the OS setting, so 'system' resolves to light for SSR and the first client render and then
  // settles in the effect below. The page itself does not flash — RootLayout's inline script sets the attribute before
  // first paint — but a toggle's icon can be a frame behind on a dark-OS machine with no stored choice.
  const [systemColorsTheme, setSystemColorsTheme] = useState<ColorsTheme>('light');

  useEffect(() => {
    const mediaQuery = window.matchMedia(DARK_COLORS_MEDIA_QUERY);
    const readMediaQuery = () => setSystemColorsTheme(mediaQuery.matches ? 'dark' : 'light');

    readMediaQuery();
    // Subscribed rather than read once so the page follows the OS while the preference is 'system'
    mediaQuery.addEventListener('change', readMediaQuery);

    return () => mediaQuery.removeEventListener('change', readMediaQuery);
  }, []);

  const colorsTheme = colorsThemePreference === 'system' ? systemColorsTheme : colorsThemePreference;

  useEffect(() => {
    // The attribute lives on <html>, above React's root, so it is written imperatively rather than rendered. Portaled
    // content (modals render into #modalPortal) inherits it from there, which is why no component needs a theme prop.
    document.documentElement.setAttribute(COLORS_THEME_ATTRIBUTE, colorsTheme);
  }, [colorsTheme]);

  const setColorsTheme = useCallback((newPreference: ColorsThemePreference) => {
    // The cookie only has to be right for the *next* server render — deliberately no router.refresh() here, which
    // would re-render the tree and resize long-lived children like the map for no benefit.
    if (newPreference === 'system') {
      // An absent cookie is how 'system' is stored, so the next server render leaves the attribute off the HTML
      Cookies.remove(COLORS_THEME_COOKIE_NAME, colorsThemeCookieAttributes());
    } else {
      Cookies.set(COLORS_THEME_COOKIE_NAME, newPreference, colorsThemeCookieAttributes());
    }

    setColorsThemePreference(newPreference);
  }, []);

  const value = useMemo(() => ({ colorsTheme, colorsThemePreference, setColorsTheme }), [colorsTheme, colorsThemePreference, setColorsTheme]);

  return <ColorsThemeContext.Provider value={value}>{children}</ColorsThemeContext.Provider>;
}
